import PropTypes from 'prop-types';
import React from 'react';

import ChevronIcon from 'Component/ChevronIcon';
import { LEFT } from 'Component/ChevronIcon/ChevronIcon.config';
import Html from 'Component/Html';
import Image from 'Component/Image';
import Link from 'Component/Link';
import MenuItem from 'Component/MenuItem';
import StoreLocatorLink from 'Component/StoreLocatorLink';
import {
    ACCOUNT_PRO_LOGIN_URL,
    ACCOUNT_STUDENT_LOGIN_URL,
    GROUP_ID_EMPLOYEE,
    GROUP_ID_PRO
} from 'Route/MyAccount/MyAccount.config';
import {
    Menu as SourceMenu
} from 'SourceComponent/Menu/Menu.component';
import { CustomerType } from 'Type/Account.type';
import { MixType, ModsType } from 'Type/Common.type';
import { noopFn } from 'Util/Common';
import { getSortedItems } from 'Util/Menu';

import './Menu.override.style';

/** @namespace PeggysagePwa/Component/Menu/Component */
export class MenuComponent extends SourceMenu {
    static propTypes = {
        ...SourceMenu.propTypes,
        mix: MixType.isRequired,
        mods: ModsType.isRequired,
        activeLevel: PropTypes.number,
        onSubMenuBackButtonClick: PropTypes.func.isRequired,
        isCustomerTypeStudentEnabled: PropTypes.bool.isRequired,
        customer: CustomerType.isRequired
    };

    static defaultProps = {
        ...SourceMenu.defaultProps,
        activeLevel: 0
    };

    /**
     * Override: remove CloseMenu on scroll
     */
    // eslint-disable-next-line @scandipwa/scandipwa-guidelines/only-render-in-component
    componentDidMount() {
        // ...
    }

    /**
     * Override: remove event listener cancelation for CloseMenu on scroll
     */
    // eslint-disable-next-line @scandipwa/scandipwa-guidelines/only-render-in-component
    componentWillUnmount() {
        // ...
    }

    renderRelatedPage(page, index) {
        const {
            content_image_thumb, page_type, title, url, page_link_type_title
        } = page;

        const pageTypes = {
            tutorial: __('Tutos et conseils'),
            news: __('News')
        };

        const pageType = pageTypes[page_type] || null;

        return (
            <div block="Menu" elem="RelatedPage" key={ index }>
                <Link
                  block="Menu"
                  elem="RelatedPageLink"
                  to={ url }
                >
                    <div block="Menu" elem="RelatedPageVisual">
                        <Image
                          src={ content_image_thumb }
                          ratio="custom"
                          mix={ {
                              block: 'Menu',
                              elem: 'VisualThumbnail',
                              mods: { isPlaceholder: !content_image_thumb }
                          } }
                          isPlaceholder={ !content_image_thumb }
                          alt={ title }
                        />
                    </div>
                    <div block="Menu" elem="RelatedPageContent">
                        { page_link_type_title && (
                            <div block="Menu" elem="RelatedPageType">{ page_link_type_title }</div>
                        ) }
                        { !page_link_type_title && pageType && (
                            <div block="Menu" elem="RelatedPageType">{ pageType }</div>
                        ) }
                        <div block="Menu" elem="RelatedPageTitle">{ title }</div>
                    </div>
                </Link>
            </div>
        );
    }

    renderRelatedPages(item) {
        const { related_pages } = item;

        if (!related_pages || !related_pages.length) {
            return null;
        }

        return (
            <div block="Menu" elem="RelatedPages">
                { related_pages.map(this.renderRelatedPage.bind(this)) }
            </div>
        );
    }

    renderDesktopContentPush(category) {
        const { device } = this.props;

        if (device.isMobile || !category.content_push || !category.content_push.length) {
            return null;
        }

        const contentPush = category.content_push[0];

        const {
            supertitle, title, page_identifier, image_url
        } = contentPush;

        return (
            <div
              role="button"
              tabIndex="-1"
              block="Menu"
              elem="SubItemWrapper"
              mods={ { isPush: true } }
            >
                <Link
                  block="Menu"
                  elem="PushLink"
                  to={ page_identifier }
                >
                    <div block="Menu" elem="Push">
                        <div block="Menu" elem="PushImageWrapper">
                            { image_url && (
                                <Image
                                  src={ image_url }
                                  ratio="custom"
                                  mix={ {
                                      block: 'Menu',
                                      elem: 'PushImage'
                                  } }
                                  alt={ title }
                                />
                            ) }
                        </div>
                        <div block="Menu" elem="PushContent">
                            <div block="Menu" elem="PushSuperTitle">
                                { supertitle }
                            </div>
                            <div block="Menu" elem="PushTitle">
                                { title }
                            </div>
                        </div>
                    </div>
                </Link>
            </div>
        );
    }

    renderAdditionalMenu(category) {
        const { closeMenu, device } = this.props;
        const { additional_menu } = category;

        if (!additional_menu) {
            return null;
        }

        return (
            <div
              block="Menu"
              elem="AdditionalMenu"
              onMouseEnter={ !device.isMobile ? closeMenu : noopFn }
            >
                <div block="Menu" elem="AdditionalMenuInner">
                    <span block="Menu" elem="AdditionalMenuMinimizedItem">+</span>
                    <Html content={ additional_menu } />
                </div>
            </div>
        );
    }

    /**
     * Override: on mobile, replace click on whole div by button
     * @param item
     * @param isSecondLevel
     */
    renderSubLevelItems(item, isSecondLevel) {
        const {
            activeMenuItemsStack,
            handleSubcategoryClick,
            onCategoryHover,
            closeMenu,
            device
        } = this.props;

        const { item_id, children } = item;

        const childrenArray = Object.values(children);
        const subcategoryMods = { type: 'subcategory', isSecondLevel };

        if (childrenArray.length && device.isMobile) {
            return (
                <div
                  key={ item_id }
                  tabIndex="0"
                  role="button"
                >
                    <MenuItem
                      activeMenuItemsStack={ activeMenuItemsStack }
                      item={ item }
                      itemMods={ { ...subcategoryMods, isExpanded: activeMenuItemsStack.includes(item_id) } }
                      onCategoryHover={ onCategoryHover }
                      closeMenu={ closeMenu }
                      isExpandable
                      isButton
                      // eslint-disable-next-line react/jsx-no-bind
                      onClick={ (e) => handleSubcategoryClick(e, item) }
                    />
                    { this.renderSubLevel(item) }
                </div>
            );
        }

        return super.renderSubLevelItems(item, isSecondLevel);
    }

    renderSubLevelMobile(category, isSecondLevel = false) {
        const { activeMenuItemsStack, onSubMenuBackButtonClick, closeMenu } = this.props;
        const { item_id, children, title } = category;
        const childrenArray = getSortedItems(Object.values(children));
        const isVisible = activeMenuItemsStack.includes(item_id);
        const subcategoryMods = { type: 'subcategory' };

        return (
            <div
              block="Menu"
              elem="SubMenu"
              mods={ { isVisible } }
              key={ item_id }
            >
                <div
                  block="Menu"
                  elem="ItemList"
                  mods={ { ...subcategoryMods } }
                >
                    <div block="Menu" elem="SubItemWrapperBack">
                        <button
                          block="Menu"
                          elem="BackButton"
                          mods={ { type: 'back' } }
                          // eslint-disable-next-line react/jsx-no-bind
                          onClick={ (e) => onSubMenuBackButtonClick(e, category) }
                        >
                            <ChevronIcon direction={ LEFT } />
                            { __('Back') }
                        </button>
                    </div>
                    <div block="Menu" elem="SubItemHeader">
                        <MenuItem
                          activeMenuItemsStack={ activeMenuItemsStack }
                          item={ category }
                          itemMods={ { ...subcategoryMods, isSecondLevel, isTitle: true } }
                          closeMenu={ closeMenu }
                          isLink
                        />
                        <MenuItem
                          activeMenuItemsStack={ activeMenuItemsStack }
                          item={ { ...category, title: __('All %s', title) } }
                          itemMods={ { ...subcategoryMods, isSecondLevel, isLink: true } }
                          closeMenu={ closeMenu }
                          isLink
                        />
                    </div>
                    { childrenArray.map((item) => this.renderSubLevelItems(item, isSecondLevel)) }
                </div>
                { this.renderAdditionalMenu(category) }
                { this.renderRelatedPages(category) }
            </div>
        );
    }

    /**
     * Override: call specific renderer for mobile, add content push on desktop
     * @param category
     * @param isSecondLevel
     * @returns {JSX.Element}
     */
    renderSubLevel(category, isSecondLevel = false) {
        const { activeMenuItemsStack, device } = this.props;
        const { item_id, children, title } = category;
        const childrenArray = getSortedItems(Object.values(children));
        const isVisible = activeMenuItemsStack.includes(item_id);
        const subcategoryMods = { type: 'subcategory' };

        if (device.isMobile) {
            return this.renderSubLevelMobile(category, isSecondLevel);
        }

        const linksCount = childrenArray.reduce((acc, list) => acc + 1 + Object.keys(list.children).length, 0);

        // eslint-disable-next-line no-magic-numbers
        const isPushContentInCols = linksCount >= 15;

        return (
            <div
              block="Menu"
              elem="SubMenu"
              mods={ { isVisible } }
              key={ item_id }
            >
                <MenuItem
                  activeMenuItemsStack={ activeMenuItemsStack }
                  item={ { ...category, title: __('All %s', title) } }
                  itemMods={ { ...subcategoryMods, isSecondLevel } }
                  isLink
                />
                <div
                  block="Menu"
                  elem="ItemList"
                  mods={ { ...subcategoryMods } }
                >
                    { childrenArray.map((item) => this.renderSubLevelItems(item, isSecondLevel)) }
                    { isPushContentInCols && this.renderDesktopContentPush(category) }
                </div>
                { !isPushContentInCols && this.renderDesktopContentPush(category) }
            </div>
        );
    }

    /**
     * Override: add related pages
     * @param item
     * @returns {JSX.Element|null}
     */
    renderSubMenuDesktopItems(item) {
        const { item_id, children } = item;

        if (!Object.keys(children).length) {
            return null;
        }

        const { activeMenuItemsStack, closeMenu } = this.props;
        const isVisible = activeMenuItemsStack.includes(item_id)/* || true */; // @TODO uncomment to develop

        if (!isVisible) {
            return null;
        }

        return (
            <div
              block="Menu"
              elem="SubCategoriesWrapper"
              mods={ { isVisible } }
              key={ item_id }
            >
                <div
                  block="Menu"
                  elem="SubCategoriesWrapperInner"
                  mods={ { isVisible } }
                >
                    <div
                      block="Menu"
                      elem="SubCategories"
                    >
                        { this.renderSubLevel(item) }
                    </div>
                    { this.renderRelatedPages(item) }
                    { this.renderAdditionalInformation(true) }
                </div>
                <div
                  block="Menu"
                  elem="Overlay"
                  mods={ { isVisible } }
                  onMouseEnter={ closeMenu }
                />
            </div>
        );
    }

    /**
     * Override: remove Compare page link
     * @param checkMobile
     * @returns {JSX.Element|null}
     */
    renderAdditionalInformation(checkMobile = false) {
        const { device } = this.props;

        if (checkMobile && !device.isMobile) {
            return null;
        }

        return (
            <div block="Menu" elem="AdditionalInformation">
                { this.renderCurrencySwitcher() }
                { this.renderStoreSwitcher() }
                { /* this.renderComparePageLink() */ }
            </div>
        );
    }

    renderProPageLink() {
        return (
            <div block="Menu" elem="ProPageLink">
                <Link
                  to={ ACCOUNT_PRO_LOGIN_URL }
                  key="pro"
                  block="Menu"
                  elem="ProButton"
                >
                    { __('Pro area') }
                </Link>
            </div>
        );
    }

    renderStudentPageLink() {
        const { isCustomerTypeStudentEnabled, customer: { group_id = 0 } = {} } = this.props;

        if (group_id === GROUP_ID_PRO || group_id === GROUP_ID_EMPLOYEE || !isCustomerTypeStudentEnabled) {
            return null;
        }

        return (
            <div block="Menu" elem="StudentPageLink">
                <Link
                  to={ ACCOUNT_STUDENT_LOGIN_URL }
                  key="student"
                  block="Menu"
                  elem="StudentButton"
                >
                    { __('Student area') }
                </Link>
            </div>
        );
    }

    renderAdditionalLinks(checkMobile = false) {
        const { device } = this.props;

        if (checkMobile && !device.isMobile) {
            return null;
        }

        return (
            <div block="Menu" elem="AdditionalLinks">
                <div block="Menu" elem="StoreLocatorLink">
                    <StoreLocatorLink />
                </div>
                { this.renderProPageLink() }
                { this.renderStudentPageLink() }
            </div>
        );
    }

    /**
     * Override: on mobile, replace click on whole div by button
     * @param item
     * @returns {JSX.Element|*}
     */
    renderFirstLevelItems(item) {
        const {
            activeMenuItemsStack,
            handleSubcategoryClick,
            onCategoryHover,
            closeMenu,
            device
        } = this.props;

        const { children, item_id } = item;
        const childrenArray = Object.values(children);
        const itemMods = { type: 'main' };

        if (childrenArray.length && device.isMobile) {
            return (
                <div
                  tabIndex="0"
                  block="Menu"
                  elem="SubCatLink"
                  role="button"
                >
                    <MenuItem
                      activeMenuItemsStack={ activeMenuItemsStack }
                      item={ item }
                      itemMods={ { ...itemMods, isExpanded: activeMenuItemsStack.includes(item_id) } }
                      onCategoryHover={ onCategoryHover }
                      closeMenu={ closeMenu }
                      isExpandable
                      isButton
                      // eslint-disable-next-line react/jsx-no-bind
                      onClick={ (e) => handleSubcategoryClick(e, item) }
                    />
                    { this.renderSubLevel(item, true) }
                </div>
            );
        }

        return super.renderFirstLevelItems(item);
    }

    /**
     * Override: add class for new products category
     * @param item
     * @returns {JSX.Element}
     */
    renderFirstLevel(item) {
        const { item_id, is_new_products } = item;

        return (
            <li
              block="Menu"
              elem="Item"
              mods={ { isNewProducts: !!is_new_products } }
              key={ item_id }
            >
                { this.renderFirstLevelItems.call(this, item) }
            </li>
        );
    }

    /**
     * Override: move additionnal infos, add new section to the top, add active level for mobile
     * @returns {JSX.Element|null}
     */
    renderTopLevel() {
        const { menu, activeLevel } = this.props;
        const categoryArray = Object.values(menu);

        if (!categoryArray.length) {
            return null;
        }

        const [{ children, title: mainCategoriesTitle }] = categoryArray;
        const childrenArray = getSortedItems(Object.values(children));

        return (
            <>
                <div
                  block="Menu"
                  elem="MainCategories"
                  role="navigation"
                  aria-label={ __('Primary') }
                  // eslint-disable-next-line no-magic-numbers
                  style={ { '--menu-mobile-transform': `-${ activeLevel * 100 }%` } }
                >
                    { this.renderAdditionalLinks(true) }
                    <ul
                      block="Menu"
                      elem="ItemList"
                      mods={ { type: 'main' } }
                      aria-label={ mainCategoriesTitle }
                    >
                        { childrenArray.map(this.renderFirstLevel.bind(this)) }
                    </ul>
                    { this.renderAdditionalMenu(categoryArray[0]) }
                    { this.renderAdditionalInformation(true) }
                </div>
                { this.renderSubMenuDesktop(children) }
            </>
        );
    }

    /**
     * Override: add mix and mods on wrapper
     * @returns {JSX.Element}
     */
    render() {
        const {
            closeMenu, mix, mods, device
        } = this.props;

        return (
            <div
              block="Menu"
              elem="MenuWrapper"
              onMouseLeave={ !device.isMobile ? closeMenu : noopFn }
              mix={ mix }
              mods={ mods }
            >
                { this.renderTopLevel() }
            </div>
        );
    }
}

export default MenuComponent;
